import { useState, useEffect } from 'react';
import styled from 'styled-components';
import CommonWrapper from '../../../components/ui_components/FounderWrapper';
import {
    apiGetAppSettings,
    apiUpdateAppSetting,
    apiGetAppSettingHistory,
    type AppSetting,
    type AppSettingHistoryEntry,
} from '../../../api/appSettingsApi';

// ─── Styled ───────────────────────────────────────────────────────────────────

const PageInner = styled.div`
  padding: 32px 24px;
  max-width: 800px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-size: 26px;
  font-weight: 700;
  color: gold;
  margin: 0 0 32px;
`;

const SettingCard = styled.div`
  background-color: #1a1a1a;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 10px;
  padding: 22px 24px;
  margin-bottom: 20px;
`;

const SettingKey = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: rgba(255,255,255,0.3);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 4px;
`;

const SettingDescription = styled.div`
  font-size: 13px;
  color: rgba(255,255,255,0.5);
  margin-bottom: 16px;
`;

const EditRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ValueInput = styled.input`
  width: 120px;
  background-color: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.15);
  border-radius: 6px;
  color: white;
  font-size: 20px;
  font-weight: 700;
  padding: 8px 12px;
  text-align: center;
  outline: none;

  &:focus {
    border-color: gold;
  }

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    opacity: 1;
  }
`;

const SaveBtn = styled.button<{ $saving: boolean }>`
  padding: 9px 20px;
  border-radius: 6px;
  border: 1px solid ${({ $saving }) => $saving ? 'rgba(255,255,255,0.1)' : 'gold'};
  background-color: ${({ $saving }) => $saving ? 'transparent' : 'rgba(255,215,0,0.12)'};
  color: ${({ $saving }) => $saving ? 'rgba(255,255,255,0.3)' : 'gold'};
  font-size: 13px;
  font-weight: 700;
  cursor: ${({ $saving }) => $saving ? 'default' : 'pointer'};
  transition: background-color 0.15s;

  &:hover:not(:disabled) {
    background-color: rgba(255,215,0,0.22);
  }
`;

const Feedback = styled.span<{ $type: 'success' | 'error' }>`
  font-size: 12px;
  color: ${({ $type }) => $type === 'success' ? '#4caf6f' : '#e05555'};
`;

const HistoryToggle = styled.button`
  margin-top: 14px;
  background: none;
  border: none;
  color: rgba(255,255,255,0.3);
  font-size: 12px;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;

  &:hover { color: rgba(255,255,255,0.6); }
`;

const HistoryList = styled.div`
  margin-top: 12px;
  border-top: 1px solid rgba(255,255,255,0.06);
  padding-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const HistoryEntry = styled.div`
  font-size: 12px;
  color: rgba(255,255,255,0.45);
  display: flex;
  gap: 10px;
  align-items: baseline;
`;

const HistoryValue = styled.span`
  font-weight: 600;
  color: rgba(255,255,255,0.75);
`;

const StatusText = styled.p`
  color: rgba(255,255,255,0.3);
  font-size: 13px;
`;

// ─── Setting row component ────────────────────────────────────────────────────

interface SettingRowProps {
    setting: AppSetting;
    onSaved: (key: string, newValue: string) => void;
}

function SettingRow({ setting, onSaved }: SettingRowProps) {
    const [draft, setDraft] = useState(setting.value);
    const [saving, setSaving] = useState(false);
    const [feedback, setFeedback] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
    const [showHistory, setShowHistory] = useState(false);
    const [history, setHistory] = useState<AppSettingHistoryEntry[] | null>(null);
    const [loadingHistory, setLoadingHistory] = useState(false);

    const isDirty = draft !== setting.value;

    const handleSave = async () => {
        if (!isDirty || saving) return;
        setSaving(true);
        setFeedback(null);
        try {
            const res = await apiUpdateAppSetting(setting.key, draft);
            onSaved(setting.key, res.data.value);
            setFeedback({ msg: 'Salvato', type: 'success' });
        } catch (err: any) {
            const msg = err?.message ?? 'Errore durante il salvataggio';
            setFeedback({ msg, type: 'error' });
            setDraft(setting.value);
        } finally {
            setSaving(false);
        }
    };

    const toggleHistory = async () => {
        if (!showHistory && history === null) {
            setLoadingHistory(true);
            try {
                const res = await apiGetAppSettingHistory(setting.key);
                setHistory(res.data);
            } catch {
                setHistory([]);
            } finally {
                setLoadingHistory(false);
            }
        }
        setShowHistory(prev => !prev);
    };

    const formatDate = (iso: string) => {
        const d = new Date(iso);
        return d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    return (
        <SettingCard>
            <SettingKey>{setting.key}</SettingKey>
            <SettingDescription>{setting.description}</SettingDescription>

            <EditRow>
                <ValueInput
                    type="number"
                    value={draft}
                    min={1}
                    onChange={e => { setDraft(e.target.value); setFeedback(null); }}
                    onKeyDown={e => { if (e.key === 'Enter') handleSave(); }}
                />
                <SaveBtn $saving={saving || !isDirty} disabled={saving || !isDirty} onClick={handleSave}>
                    {saving ? 'Salvataggio...' : 'Salva'}
                </SaveBtn>
                {feedback && <Feedback $type={feedback.type}>{feedback.msg}</Feedback>}
            </EditRow>

            <HistoryToggle onClick={toggleHistory}>
                {showHistory ? 'Nascondi storico' : 'Vedi storico modifiche'}
            </HistoryToggle>

            {showHistory && (
                <HistoryList>
                    {loadingHistory && <StatusText>Caricamento...</StatusText>}
                    {!loadingHistory && history?.length === 0 && (
                        <StatusText>Nessuna modifica registrata.</StatusText>
                    )}
                    {history?.map((entry, i) => (
                        <HistoryEntry key={i}>
                            <span>{formatDate(entry.changed_at)}</span>
                            <span>
                                <HistoryValue>{entry.old_value}</HistoryValue>
                                {' → '}
                                <HistoryValue>{entry.new_value}</HistoryValue>
                            </span>
                            <span>da {entry.changed_by.username}</span>
                        </HistoryEntry>
                    ))}
                </HistoryList>
            )}
        </SettingCard>
    );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const AppSettingsPage: React.FC = () => {
    const [settings, setSettings] = useState<AppSetting[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        apiGetAppSettings()
            .then(res => setSettings(res.data))
            .catch(() => setError(true))
            .finally(() => setIsLoading(false));
    }, []);

    const handleSaved = (key: string, newValue: string) => {
        setSettings(prev => prev.map(s => s.key === key ? { ...s, value: newValue } : s));
    };

    return (
        <CommonWrapper>
            <PageInner>
                <PageTitle>Impostazioni App</PageTitle>

                {isLoading && <StatusText>Caricamento...</StatusText>}
                {error && <StatusText>Errore nel caricamento delle impostazioni.</StatusText>}

                {!isLoading && !error && settings.map(setting => (
                    <SettingRow key={setting.key} setting={setting} onSaved={handleSaved} />
                ))}
            </PageInner>
        </CommonWrapper>
    );
};

export default AppSettingsPage;
